import { mutateUpload, SIGNED_OUT_MESSAGE, UPLOAD_TIMEOUT_MS, type Ack } from './api'
import { announce, getDraft, putDraft, removeDraft, type MediaState } from './media-store'
import { el, toast } from './ui'

// Voice note on a visit: tap once to record, tap again to stop and send.
// The server transcribes it (Groq Whisper) and appends the text to the
// visit's note; this half owns the microphone, the button's states, and
// what happens when the answer never comes back.
//
// Recording mechanics are copied from the dashboard's mobile mic (the
// copy that survived real iPhones — see brain-dashboard static/mobile/app.js):
//   - getUserMedia({ audio: true }) and NOTHING else in the constraints, and
//     it must be the FIRST audio-touching call inside the tap handler — it
//     carries the page's one piece of user activation, and any audio work in
//     front of it makes iOS refuse the mic silently;
//   - MediaRecorder with an ['audio/mp4','audio/webm'] preference (iOS records
//     AAC-in-mp4; Android webm), chunks every 500 ms;
//   - a hard cap on length, so a forgotten recording cannot grow forever.
// Never auto-send on silence — this is a note, not a chat.

const MIC_MAX_S = 90
const MIME_PREFERENCE = ['audio/mp4', 'audio/webm']
// The release-everything backstop: a hair past the upload fetch's own 40 s
// timeout, so a lost ANSWER (phone slept, tab backgrounded) still unpins the
// button even though the fetch itself is gone quiet.
const SEND_WATCHDOG_MS = UPLOAD_TIMEOUT_MS + 5_000

// Signed in as another account now (me.ts announces it, and reloads the page
// unless someone objects). A recording not yet sent exists only in this
// page's memory, so while one is being made, sent or kept for Retry the
// reload is refused and the button says what to do instead. Nothing is sent
// from these screens meanwhile (api.ts); it goes once the first account is
// back.
const ACCOUNT_CHANGED =
  "You're signed in as another account now. Sign back in as the first one to send this voice note, or discard it."
export function hasPendingVoiceDrafts(): boolean { return [...recordingsHeld].some((r) => r.holds()) }
const recordingsHeld = new Set<{ holds: () => boolean; tell: () => void; restore: () => void }>()

window.addEventListener('dropby:identity-changed', (e) => {
  let held = false
  for (const r of [...recordingsHeld]) {
    if (!r.holds()) {
      recordingsHeld.delete(r)
      continue
    }
    held = true
    r.tell()
  }
  if (held) e.preventDefault()
})

// Only what this account can do changed (several jobs switched): a recording still being made or sent is still this
// account's; the page's redraw waits for it (me.ts).
window.addEventListener('dropby:capability-changed', (e) => {
  for (const r of [...recordingsHeld]) if (r.holds()) { e.preventDefault(); return }
})

// The first account is back (me.ts): the waiting recordings can go again.
window.addEventListener('dropby:identity-restored', () => {
  for (const r of recordingsHeld) r.restore()
})

interface VoiceRow {
  id: number
  version: number
  note: string | null
}

export interface VoiceNoteOptions {
  clean: { id: number; version: number }
  onSaved: (row: VoiceRow) => void
  // The finish panel's feedback box (2026-09-23) words the button for a first-
  // time user: what it does when idle, and that a second tap stops and sends.
  // Without these the card button reads exactly as it always has.
  idleLabel?: string
  stopLabel?: string
  // Told on every change of state, so a panel can hold its own Save while a
  // note is recording or sending (both write the same visit row).
  onStateChange?: (state: 'idle' | 'recording' | 'sending' | 'retry') => void
}

type State = 'idle' | 'recording' | 'sending' | 'retry'

export function attachVoiceNote(button: HTMLButtonElement, opts: VoiceNoteOptions): void {
  const me = {} // this button, so it can ignore what it announced itself
  // The drawn microphone (styles.css .ic-mic); it turns into a stop square
  // while recording (.voice-recording), so the words can stay plain.
  button.classList.add('ic', 'ic-mic')
  let state: State = 'idle'
  let rec: MediaRecorder | null = null
  let stream: MediaStream | null = null
  let chunks: Blob[] = []
  let mime = 'audio/mp4'
  let t0 = 0
  let timer = 0
  let autoStop = 0
  let sendAfterStop = false
  // The failed upload, kept in memory for Retry. One Idempotency-Key per
  // recording — a retry is the SAME request, not a second one. version is
  // the row version this component currently believes in: it starts from the
  // card's snapshot and is then UPDATED from every server answer, so a second
  // recording on the same card sends the version the first save produced
  // (re-snapshotting the stale original here is what made the second note
  // 409 and its recording die).
  let blob: Blob | null = null
  let key: string | null = null
  let version = opts.clean.version
  let lastError = ''
  let discardBtn: HTMLButtonElement | null = null

  // Held while a recording exists only here: being made, being sent, or kept
  // for Retry on a button still on screen (a detached one cannot be retried).
  const held = {
    holds: (): boolean => state === 'recording' || state === 'sending' || (state === 'retry' && blob !== null && button.isConnected),
    tell: (): void => {
      if (state !== 'retry') return
      lastError = ACCOUNT_CHANGED
      render()
    },
    restore: (): void => {
      if (state !== 'retry' || lastError !== ACCOUNT_CHANGED) return
      lastError = 'Retry'
      render()
    },
  }

  // Inside the iPhone app the microphone is the app's own switch (Astra's store check, 4 Nov 2026).
  const MIC_BLOCKED = typeof window !== 'undefined' && 'RoundlyIOS' in window
    ? 'Microphone blocked: open Settings, Apps, Roundly and turn Microphone on.'
    : 'Microphone blocked: Roundly is a web app, so it is not in Settings > Privacy > Microphone. ' +
      'Use Settings > Apps > Safari > Microphone.'

  function ext(): string {
    if (mime.includes('mp4')) return 'm4a'
    if (mime.includes('webm')) return 'webm'
    if (mime.includes('ogg')) return 'ogg'
    if (mime.includes('wav')) return 'wav'
    if (mime.includes('mpeg')) return 'mp3'
    return 'm4a'
  }

  function label(): string {
    if (state === 'recording') {
      const s = Math.floor((Date.now() - t0) / 1000)
      const clock = `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
      return opts.stopLabel ? `${opts.stopLabel} · ${clock}` : clock
    }
    if (state === 'sending') return 'Transcribing…'
    if (state === 'retry') return lastError || 'Retry'
    return opts.idleLabel ?? 'Voice note'
  }

  function render(): void {
    opts.onStateChange?.(state)
    button.classList.toggle('voice-recording', state === 'recording')
    button.classList.toggle('voice-error', state === 'retry')
    button.disabled = state === 'sending'
    button.textContent = label()
    if (state === 'retry' && !discardBtn) {
      discardBtn = el('button', { type: 'button', class: 'voice-discard' }, 'Discard')
      discardBtn.addEventListener('click', () => {
        cleanup()
        announce({ kind: 'audio', cleanId: opts.clean.id, what: 'cleared', from: me })
        render()
      })
      button.after(discardBtn)
    }
    if (state !== 'retry' && discardBtn) {
      discardBtn.remove()
      discardBtn = null
    }
  }

  function cleanup(): void {
    // Sent, discarded or refused for good: nothing is kept on the phone.
    if (blob) void removeDraft('audio', opts.clean.id)
    if (timer) clearInterval(timer)
    if (autoStop) clearTimeout(autoStop)
    timer = 0
    autoStop = 0
    stream?.getTracks().forEach((t) => t.stop())
    stream = null
    rec = null
    chunks = []
    blob = null
    key = null
    sendAfterStop = false
    lastError = ''
    state = 'idle'
    recordingsHeld.delete(held)
  }

  function keepForRetry(error: string): void {
    if (timer) clearInterval(timer)
    if (autoStop) clearTimeout(autoStop)
    timer = 0
    autoStop = 0
    stream?.getTracks().forEach((t) => t.stop())
    stream = null
    rec = null
    chunks = []
    sendAfterStop = false
    lastError = error
    state = 'retry'
  }

  // This button's own send failed: kept for Retry, and every other button for
  // the job is told so it stops showing "Transcribing…".
  function failed(error: string): void {
    keepForRetry(error)
    announce({ kind: 'audio', cleanId: opts.clean.id, what: 'unsent', error, from: me })
  }

  let clashRetries = 0
  async function send(): Promise<void> {
    if (!blob || !key) return
    // The job as the screen knows it now: a card passes its own visit object,
    // which a Start, Finish or payment moves on as they are saved.
    if (typeof opts.clean.version === 'number' && opts.clean.version > version) version = opts.clean.version
    state = 'sending'
    announce({ kind: 'audio', cleanId: opts.clean.id, what: 'sending', from: me })
    render()
    // A hung transcription must not freeze the button forever: mutateUpload
    // hard-times-out at SEND_TIMEOUT_MS, but a lost answer (phone slept, tab
    // backgrounded) can still leave 'sending' pinned — release it the same
    // way the dashboard's mic releases a stuck send.
    const startedAt = Date.now()
    const watchdog = window.setInterval(() => {
      if (state !== 'sending') {
        clearInterval(watchdog)
      } else if (Date.now() - startedAt > SEND_WATCHDOG_MS) {
        clearInterval(watchdog)
        failed('That never came back — retry?')
        render()
      }
    }, 1000)
    let ack: Ack<VoiceRow>
    try {
      ack = await mutateUpload<VoiceRow>({
        path: `/api/cleans/${opts.clean.id}/voice`,
        key,
        fields: { version: String(version) },
        file: blob,
        filename: `voice-note.${ext()}`,
      })
    } catch {
      ack = { ok: false, status: 0, error: 'no connection' }
    }
    clearInterval(watchdog)
    if (state !== 'sending') return // the watchdog already released the button
    if (ack.ok) {
      // The save bumped the row: THIS component now believes the returned
      // version, so the next recording on the same card cannot 409 against
      // the note it just wrote.
      const saved = ack.row as VoiceRow
      if (typeof saved?.version === 'number') version = saved.version
      cleanup()
      render()
      announce({ kind: 'audio', cleanId: opts.clean.id, what: 'sent', row: saved, from: me })
      // The note is on the visit either way; say so honestly when the audio
      // itself could not be kept (the owner would otherwise expect a clip).
      toast((ack as { audio_saved?: boolean }).audio_saved === false ? 'Note added (the recording itself was not kept)' : 'Note added')
      opts.onSaved(saved)
      return
    }
    if (ack.signedOut) {
      window.alert(SIGNED_OUT_MESSAGE)
      failed('Sign in, then retry')
      render()
      return
    }
    if (ack.code === 'business_mismatch') {
      // Signed in as another account now: this recording is the first one's.
      // Nothing was written, so it keeps its key and version; it goes once
      // that account is back (and the page does not reload over it).
      failed(ACCOUNT_CHANGED)
      render()
      return
    }
    if (ack.status === 0) {
      failed('No signal — try again when you have signal')
      render()
      return
    }
    if (ack.status === 502) {
      failed('Transcribing failed — retry?')
      render()
      return
    }
    if (ack.status === 422) {
      // 'nothing heard' — the same bytes will answer the same way; kept only
      // because a noisy van clip is worth one human judgment call.
      failed('Nothing heard — retry or discard')
      render()
      return
    }
    if (ack.status === 409) {
      // Edited elsewhere while we recorded. The server wrote NOTHING (the
      // conflict fires before any write), so the key is still unused and the
      // retry may keep it — it re-sends the same bytes with the CONFLICT
      // row's version, which the server handed back for exactly this. The
      // recording survives; Retry or Discard is the user's call.
      const current = ack.current as { version?: unknown } | undefined
      const fresh = typeof current?.version === 'number' && current.version !== version
      if (typeof current?.version === 'number') version = Math.max(current.version, opts.clean.version ?? 0)
      if (blob && key) void putDraft({ kind: 'audio', cleanId: opts.clean.id, blob, key, mime, version })
      // The usual cause is the worker's own tap a moment before (Slide to
      // finish, a payment, a note), which moved the job on while this was on
      // its way (found 2026-09-24). Nothing was written, so it goes again at
      // once with the job's version, one time; only a second clash waits for
      // the worker.
      if (fresh && clashRetries < 2) {
        clashRetries++
        window.setTimeout(() => void send(), 400)
        return
      }
      failed('Edited elsewhere — retry?')
      render()
      return
    }
    // 400 (invalid audio/note-full), 401/403 (not allowed): retrying cannot help.
    window.alert(ack.error ?? 'The note was not saved')
    cleanup()
    render()
    announce({ kind: 'audio', cleanId: opts.clean.id, what: 'cleared', from: me })
  }

  function stopAndSend(): void {
    if (state !== 'recording' || !rec) return
    sendAfterStop = true
    if (timer) clearInterval(timer)
    timer = 0
    if (autoStop) clearTimeout(autoStop)
    autoStop = 0
    rec.stop()
  }

  let starting = false
  async function start(): Promise<void> {
    // typeof guards: TS's lib says these always exist, real old browsers say
    // otherwise — the alert below must name the fix, not throw.
    if (typeof navigator.mediaDevices?.getUserMedia !== 'function' || typeof MediaRecorder === 'undefined') {
      window.alert('Recording needs Safari or Chrome on this phone.')
      return
    }
    // One start at a time: a second tap while the phone asks for the microphone would open a second one that Stop never
    // reaches (Astra, 24 Oct 2026).
    if (starting) return
    let captured: MediaStream
    // Marked while the phone asks for the microphone, so a screen that holds things still for a recording (Today's stop
    // sheet) holds them from the tap, not only once it records.
    starting = true
    button.classList.toggle('voice-starting', true)
    try {
      // NOTHING may touch audio above this line — see the header comment.
      captured = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch {
      starting = false
      button.classList.toggle('voice-starting', false)
      window.alert(MIC_BLOCKED)
      return
    }
    starting = false
    button.classList.toggle('voice-starting', false)
    let recorder: MediaRecorder
    try {
      const preferred = MIME_PREFERENCE.find((m) => MediaRecorder.isTypeSupported(m))
      recorder = preferred ? new MediaRecorder(captured, { mimeType: preferred }) : new MediaRecorder(captured)
    } catch {
      recorder = new MediaRecorder(captured)
    }
    stream = captured
    rec = recorder
    // Recorders report parameterised types ('audio/mp4;codecs=opus') — the
    // server matches on the bare type, so strip the parameters here or the
    // upload is refused for a mime the recorder never really chose.
    mime = (recorder.mimeType || 'audio/mp4').split(';')[0].trim().toLowerCase()
    chunks = []
    t0 = Date.now()
    key = crypto.randomUUID()
    blob = null
    sendAfterStop = false
    clashRetries = 0
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size) chunks.push(e.data)
    }
    recorder.onstop = () => {
      const built = new Blob(chunks, { type: mime })
      chunks = []
      if (sendAfterStop && built.size > 0) {
        blob = built
        // Kept on the phone before it is sent (media-store.ts): a phone that
        // puts the page away mid-send or with no signal no longer loses it.
        if (key) void putDraft({ kind: 'audio', cleanId: opts.clean.id, blob: built, key, mime, version })
        void send()
      } else {
        cleanup()
        render()
      }
    }
    recorder.start(500)
    state = 'recording'
    recordingsHeld.add(held)
    render()
    timer = window.setInterval(() => {
      // Navigating away mid-recording must not leave the mic live: the
      // detached button is the signal the screen has moved on.
      if (!button.isConnected) {
        // Stop and SEND: the recorder's onstop still fires after the button is
        // gone, and it is what hands the clip to send(). Cleaning up here
        // first wiped the chunks before onstop ran, so a note recorded while
        // the day re-rendered was lost without a word.
        window.clearInterval(timer!)
        stopAndSend()
        return
      }
      if (state === 'recording') render()
    }, 500)
    autoStop = window.setTimeout(stopAndSend, MIC_MAX_S * 1000)
  }

  // What another button for this job, or the background send, did with its
  // recording (media-store.ts announce): every button agrees, and Discard is
  // never offered while a copy is on its way.
  const onState = (e: Event): void => {
    const d = (e as CustomEvent<MediaState>).detail
    if (!d || d.kind !== 'audio' || d.cleanId !== opts.clean.id || d.from === me) return
    if (!button.isConnected) {
      window.removeEventListener('roundly:media-state', onState)
      return
    }
    if (d.what === 'sending') {
      if (state === 'retry') {
        state = 'sending'
        render()
      }
      return
    }
    if (d.what === 'unsent') {
      if (state === 'sending' && blob) {
        keepForRetry(d.error || 'Voice note not sent yet. Tap to send')
        render()
      }
      return
    }
    if (state !== 'retry' && state !== 'sending') return
    const saved = d.what === 'sent' ? (d.row as VoiceRow | null) : null
    if (saved && typeof saved.version === 'number') version = saved.version
    blob = null // already dealt with; nothing to remove from the phone here
    cleanup()
    render()
    if (saved) opts.onSaved(saved)
  }
  window.addEventListener('roundly:media-state', onState)

  // A recording kept on the phone from an earlier page (never sent) comes
  // back as a Retry, with its own key and the version it was recorded on.
  void getDraft('audio', opts.clean.id).then((kept) => {
    if (!kept || state !== 'idle' || !button.isConnected) return
    blob = kept.blob
    key = kept.key
    if (kept.mime) mime = kept.mime
    if (typeof kept.version === 'number') version = kept.version
    keepForRetry('Voice note not sent yet. Tap to send')
    recordingsHeld.add(held)
    render()
  })

  button.addEventListener('click', () => {
    if (state === 'recording') {
      stopAndSend()
      return
    }
    if (state === 'retry') {
      void send()
      return
    }
    if (state !== 'idle') return
    void start()
  })

  render()
}
